import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation as CapacitorScreenOrientation } from '@capacitor/screen-orientation';

@Injectable({
  providedIn: 'root',
})
export class ChartFullscreenService {
  private webFullscreenActive = false;

  async enterLandscape(): Promise<void> {
    try {
      if (this.canUseNativeOrientation()) {
        // Force landscape (Android maps this to SCREEN_ORIENTATION_LANDSCAPE).
        await CapacitorScreenOrientation.lock({ orientation: 'landscape' });
        return;
      }

      await this.enterWebLandscape();
    } catch (err) {
      console.warn('[ChartFullscreen] Failed to lock landscape', err);
    }
  }

  async exitLandscape(): Promise<void> {
    try {
      if (this.canUseNativeOrientation()) {
        // Restore portrait first so the phone does not stay landscape after unlock.
        try {
          await CapacitorScreenOrientation.lock({ orientation: 'portrait' });
        } catch {
          // Some devices may reject portrait lock; unlock still restores free rotation.
        }
        await CapacitorScreenOrientation.unlock();
      } else {
        await this.exitWebLandscape();
      }
    } catch (err) {
      console.warn('[ChartFullscreen] Failed to unlock orientation', err);
    }
  }

  scheduleReflow(
    chart:
      | {
          reflow?: () => void;
          setSize?: (w?: number | null, h?: number | null, animate?: boolean) => void;
        }
      | null
      | undefined
  ): void {
    if (!chart) {
      return;
    }
    const apply = () => {
      if (chart.setSize) {
        chart.setSize(null, null, false);
      } else if (chart.reflow) {
        chart.reflow();
      }
    };
    // Orientation change animation needs a beat before Highcharts measures layout.
    setTimeout(apply, 100);
    setTimeout(apply, 400);
    setTimeout(apply, 800);
  }

  private canUseNativeOrientation(): boolean {
    return (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable('ScreenOrientation')
    );
  }

  private async enterWebLandscape(): Promise<void> {
    const docEl = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    if (!document.fullscreenElement && docEl.requestFullscreen) {
      await docEl.requestFullscreen();
      this.webFullscreenActive = true;
    } else if (!document.fullscreenElement && docEl.webkitRequestFullscreen) {
      await Promise.resolve(docEl.webkitRequestFullscreen());
      this.webFullscreenActive = true;
    }

    const orientation = screen.orientation as {
      lock?: (o: OrientationLockType) => Promise<void>;
    };
    if (orientation?.lock) {
      await orientation.lock('landscape');
    }
  }

  private async exitWebLandscape(): Promise<void> {
    try {
      const orientation = screen.orientation as {
        unlock?: () => void;
        lock?: (o: OrientationLockType) => Promise<void>;
      };
      if (orientation?.lock) {
        try {
          await orientation.lock('portrait');
        } catch {
          // ignore
        }
      }
      orientation?.unlock?.();
    } catch {
      // ignore
    }

    if (this.webFullscreenActive && document.fullscreenElement && document.exitFullscreen) {
      try {
        await document.exitFullscreen();
      } catch {
        // ignore
      }
    }
    this.webFullscreenActive = false;
  }
}
